/**
 * Pagination, filtering and sorting conventions.
 *
 * Cursor pagination is the default: offset pagination degrades badly on deep
 * pages once the confirmations table reaches millions of rows, because the
 * database must count past every skipped row. Offset remains available where a
 * page-number UI is genuinely required (admin tables), and is capped.
 *
 * Traces: API-005, PERF-002, SRCH-003, SRCH-004, DASH-061.
 */

import { z } from 'zod';

export const MAX_PAGE_SIZE = 100;
export const DEFAULT_PAGE_SIZE = 25;
/** Offset pagination beyond this point is refused; use a cursor instead. */
export const MAX_OFFSET = 10_000;

export const cursorPaginationSchema = z.object({
  cursor: z.string().min(1).max(512).optional(),
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
});
export type CursorPagination = z.infer<typeof cursorPaginationSchema>;

export const offsetPaginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
});
export type OffsetPagination = z.infer<typeof offsetPaginationSchema>;

export interface CursorPageMeta {
  readonly nextCursor: string | null;
  readonly hasMore: boolean;
  readonly limit: number;
}

export interface OffsetPageMeta {
  readonly page: number;
  readonly limit: number;
  readonly totalItems: number;
  readonly totalPages: number;
}

export interface Paginated<T, M = CursorPageMeta> {
  readonly data: readonly T[];
  readonly meta: { readonly pagination: M };
}

/**
 * Sort specification.
 *
 * Only whitelisted columns are sortable, and every whitelisted column must be
 * index-backed — an unindexed sort is a full scan waiting to happen. The
 * whitelist is declared per endpoint, never taken from the client.
 */
export const sortDirectionSchema = z.enum(['asc', 'desc']);
export type SortDirection = z.infer<typeof sortDirectionSchema>;

export interface SortSpec<TField extends string = string> {
  readonly field: TField;
  readonly direction: SortDirection;
}

/**
 * Parses `?sort=-lastActivityAt` into a validated spec.
 * A leading `-` means descending. Unknown fields are rejected, not ignored.
 */
export function parseSort<const TAllowed extends readonly string[]>(
  raw: string | undefined,
  allowed: TAllowed,
  fallback: SortSpec<TAllowed[number]>,
): SortSpec<TAllowed[number]> {
  if (raw === undefined || raw.length === 0) return fallback;

  const descending = raw.startsWith('-');
  const field = descending ? raw.slice(1) : raw;

  if (!allowed.includes(field)) {
    throw new Error(`Unsortable field: ${field}. Allowed: ${allowed.join(', ')}`);
  }

  return {
    field: field as TAllowed[number],
    direction: descending ? 'desc' : 'asc',
  };
}

/**
 * Opaque keyset cursor.
 *
 * Encodes the sort value and the tiebreaker id of the last row on the page.
 * Base64url keeps it URL-safe. It is deliberately opaque: clients must not
 * construct or mutate cursors, and the server validates the decoded shape.
 */
export interface CursorPayload {
  readonly v: string;
  readonly id: string;
}

export function encodeCursor(payload: CursorPayload): string {
  const json = JSON.stringify(payload);
  return btoa(json).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function decodeCursor(cursor: string): CursorPayload | null {
  try {
    const padded = cursor.replace(/-/g, '+').replace(/_/g, '/');
    const parsed: unknown = JSON.parse(atob(padded));
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      typeof (parsed as CursorPayload).v !== 'string' ||
      typeof (parsed as CursorPayload).id !== 'string'
    ) {
      return null;
    }
    return parsed as CursorPayload;
  } catch {
    return null;
  }
}

/** Common date-range filter, validated so `from` never post-dates `to`. */
export const dateRangeSchema = z
  .object({
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
  })
  .refine((r) => r.from === undefined || r.to === undefined || r.from <= r.to, {
    message: 'from must not be later than to',
    path: ['from'],
  });
export type DateRange = z.infer<typeof dateRangeSchema>;
